import {
  keepPreviousData,
  type QueryKey,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  AdminCategoryQuery,
  CustomerListQuery,
  OrderQuery,
  CustomerStatus,
  DeliveryStatus,
  OrderStatus,
  ProductStatus,
} from "@milqit/shared-types";
import type {
  AdminCustomerValues,
  AisleLandingValues,
  CategoryFormValues,
  DeliveryAssignmentValues,
  OrderStatusUpdateValues,
  ProductFormValues,
  StockAdjustmentValues,
} from "@milqit/validations";

import * as catalog from "./api/catalog";
import * as catalogImport from "./api/catalog-import";
import * as insights from "./api/insights";
import * as ops from "./api/operations";
import * as summaries from "./api/summaries";
import { usePagedList } from "./paged-list";
import type { SheetRow } from "./spreadsheet";

/** A list query's filters: everything but the page, which `usePagedList` walks. */
export type ListFilters<Q> = Omit<Q, "page" | "pageSize">;

/** The key of one filtered, paged list under an area's key (prefix invalidation still reaches it). */
export const listKey = <F extends object>(area: readonly unknown[], filters: F) =>
  [...area, "list", filters] as const;

/**
 * The first segment of every admin query key, one per area. Invalidating a root refreshes every
 * query under it (lists, details, summaries), so mutations name roots, never bare strings.
 */
export const roots = {
  dashboard: ["dashboard"] as const,
  navCounts: ["nav-counts"] as const,
  revenue: ["revenue"] as const,
  topProducts: ["top-products"] as const,
  products: ["products"] as const,
  product: ["product"] as const,
  categories: ["categories"] as const,
  inventory: ["inventory"] as const,
  adjustments: ["adjustments"] as const,
  orders: ["orders"] as const,
  order: ["order"] as const,
  customers: ["customers"] as const,
  customer: ["customer"] as const,
  deliveries: ["deliveries"] as const,
  agents: ["agents"] as const,
  subscriptions: ["subscriptions"] as const,
  planDemand: ["plan-demand"] as const,
  liveOrders: ["live-orders"] as const,
  pickList: ["pick-list"] as const,
  packing: ["packing"] as const,
  riders: ["riders"] as const,
  hubs: ["hubs"] as const,
  banners: ["banners"] as const,
  campaigns: ["campaigns"] as const,
  templates: ["templates"] as const,
  homeSections: ["home-sections"] as const,
  refunds: ["refunds"] as const,
  wallet: ["wallet"] as const,
  invoices: ["invoices"] as const,
  invoice: ["invoice"] as const,
  tickets: ["tickets"] as const,
  ticket: ["ticket"] as const,
  media: ["media"] as const,
  audience: ["audience"] as const,
};

export const keys = {
  dashboard: roots.dashboard,
  navCounts: roots.navCounts,
  revenue: (days: number) => [...roots.revenue, days] as const,
  topProducts: roots.topProducts,
  products: roots.products,
  product: (id: string) => [...roots.product, id] as const,
  categories: roots.categories,
  inventory: roots.inventory,
  adjustments: (productId: string) => [...roots.adjustments, productId] as const,
  orders: roots.orders,
  order: (id: string) => [...roots.order, id] as const,
  customers: roots.customers,
  customer: (id: string) => [...roots.customer, id] as const,
  deliveries: (day: ops.DeliveryQuery["day"]) => [...roots.deliveries, day] as const,
  agents: roots.agents,
};

/* Queries ---------------------------------------------------------------- */

export const useDashboard = () =>
  useQuery({ queryKey: keys.dashboard, queryFn: ops.getDashboardSummary });
export const useNavCounts = () =>
  useQuery({ queryKey: keys.navCounts, queryFn: insights.getNavCounts, refetchInterval: 60_000 });
export const useRevenueSeries = (days = 30) =>
  useQuery({ queryKey: keys.revenue(days), queryFn: () => insights.getRevenueSeries(days) });
export const useTopProducts = () =>
  useQuery({ queryKey: keys.topProducts, queryFn: () => insights.getTopProducts(5) });

export const useProductList = (filters: ListFilters<catalog.ProductListQuery>) =>
  usePagedList({
    queryKey: listKey(keys.products, filters),
    fetchPage: (paging) => catalog.listProducts({ ...filters, ...paging }),
    getRowId: (product) => product.id,
  });
/**
 * The whole catalogue, for pickers and matching (plan items, catalogue import). It is small (a
 * shop's products, not its orders), so it's read in full, page by page.
 */
/** Products a milk plan can hold: active and subscribable (a short list, so one page). */
export const usePlanProducts = () =>
  useQuery({
    queryKey: [...keys.products, "plan-options"],
    queryFn: () =>
      catalog.listProducts({ status: "active", subscribable: true, page: 1, pageSize: 100 }),
    select: (page) => page.items,
  });
export const useProduct = (id: string) =>
  useQuery({ queryKey: keys.product(id), queryFn: () => catalog.getProduct(id) });
export const useCategories = (query: AdminCategoryQuery = {}, enabled = true) =>
  useQuery({
    queryKey: [...keys.categories, query],
    queryFn: () => catalog.listCategories(query),
    placeholderData: keepPreviousData,
    enabled,
  });
export const useInventoryList = (filters: ListFilters<catalog.InventoryQuery>) =>
  usePagedList({
    queryKey: listKey(keys.inventory, filters),
    fetchPage: (paging) => catalog.listInventory({ ...filters, ...paging }),
    getRowId: (row) => row.productId,
  });
export const useStockAdjustments = (productId: string | null) =>
  usePagedList({
    queryKey: keys.adjustments(productId ?? "none"),
    fetchPage: (paging) => insights.listStockAdjustments(productId ?? "", paging),
    getRowId: (adjustment) => adjustment.id,
    enabled: Boolean(productId),
  });

export const useOrderList = (filters: ListFilters<OrderQuery>, enabled = true) =>
  usePagedList({
    queryKey: listKey(keys.orders, filters),
    fetchPage: (paging) => ops.listOrders({ ...filters, ...paging }),
    getRowId: (order) => order.id,
    enabled,
  });
/** A product's units sold and revenue (the API adds up every order that wasn't cancelled). */
export const useProductSales = (productId: string) =>
  useQuery({
    queryKey: [...roots.orders, "product-sales", productId],
    queryFn: () => summaries.getProductSales(productId),
  });
export const useOrder = (id: string | null) =>
  useQuery({
    queryKey: keys.order(id ?? "none"),
    queryFn: () => ops.getOrder(id ?? ""),
    enabled: Boolean(id),
  });
export const useCustomerList = (filters: ListFilters<CustomerListQuery>, enabled = true) =>
  usePagedList({
    queryKey: listKey(keys.customers, filters),
    fetchPage: (paging) => ops.listCustomers({ ...filters, ...paging }),
    getRowId: (customer) => customer.id,
    enabled,
  });
/** The customers page's tiles: how many households match each count (one-row requests). */
export function useCustomerTiles() {
  const count = (filters: ListFilters<CustomerListQuery>) => ({
    queryKey: [...keys.customers, "count", filters],
    queryFn: () =>
      ops.listCustomers({ ...filters, page: 1, pageSize: 1 }).then((page) => page.total),
  });
  return useQueries({
    queries: [
      count({ status: "all" }),
      count({ status: "all", plan: "with" }),
      count({ status: "paused" }),
      count({ status: "blocked" }),
    ],
    combine: ([households, onPlan, paused, blocked]) => ({
      isPending: [households, onPlan, paused, blocked].some((query) => query.isPending),
      // A failed count must not read as a real zero.
      isError: [households, onPlan, paused, blocked].some((query) => query.isError),
      refetch: () => [households, onPlan, paused, blocked].forEach((query) => void query.refetch()),
      households: households?.data ?? 0,
      onPlan: onPlan?.data ?? 0,
      paused: paused?.data ?? 0,
      blocked: blocked?.data ?? 0,
    }),
  });
}
export const useCustomer = (id: string | null) =>
  useQuery({
    queryKey: keys.customer(id ?? "none"),
    queryFn: () => ops.getCustomer(id ?? ""),
    enabled: Boolean(id),
  });
export const useDeliveryList = (filters: ListFilters<ops.DeliveryQuery>) =>
  usePagedList({
    queryKey: listKey(keys.deliveries(filters.day), filters),
    fetchPage: (paging) => ops.listDeliveries({ ...filters, ...paging }),
    getRowId: (delivery) => delivery.id,
  });
/** Each partner's drops on a morning and how many are delivered (counted by the API). */
export const useAgentLoad = (day: ops.DeliveryQuery["day"]) =>
  useQuery({
    queryKey: [...keys.deliveries(day), "agent-load"],
    queryFn: () => summaries.getAgentLoad(day),
  });
/** The drop for one order (its number finds it). */
export const useOrderDelivery = (order: { id: string; number: string }) =>
  useQuery({
    queryKey: [...keys.deliveries("all"), "order", order.id],
    queryFn: () =>
      ops
        .listDeliveries({ day: "all", status: "all", q: order.number, page: 1, pageSize: 10 })
        .then((page) => page.items.find((delivery) => delivery.orderId === order.id) ?? null),
  });
/** Products, orders and customers matching what staff typed in ⌘K (a few of each). */
export function useRecordSearch(term: string) {
  const enabled = term.length >= 2;
  const first = { page: 1, pageSize: 5, q: term };
  return useQueries({
    queries: [
      {
        queryKey: [...keys.products, "search", term],
        queryFn: () => catalog.listProducts({ ...first, status: "all" }),
        enabled,
      },
      {
        queryKey: [...keys.orders, "search", term],
        queryFn: () => ops.listOrders({ ...first, status: "all" }),
        enabled,
      },
      {
        queryKey: [...keys.customers, "search", term],
        queryFn: () => ops.listCustomers({ ...first, status: "all" }),
        enabled,
      },
    ],
    combine: ([products, orders, customers]) => ({
      products: products?.data?.items ?? [],
      orders: orders?.data?.items ?? [],
      customers: customers?.data?.items ?? [],
    }),
  });
}
export const useAgents = () => useQuery({ queryKey: keys.agents, queryFn: ops.listAgents });

/* Mutations -------------------------------------------------------------- */

export function useInvalidate() {
  const client = useQueryClient();
  return (...areas: QueryKey[]) =>
    Promise.all(areas.map((queryKey) => client.invalidateQueries({ queryKey })));
}

const CATALOG = [
  roots.products,
  roots.product,
  roots.inventory,
  roots.dashboard,
  roots.categories,
  roots.navCounts,
  roots.topProducts,
];
const ORDERS = [
  roots.orders,
  roots.order,
  roots.deliveries,
  roots.dashboard,
  roots.customer,
  roots.customers,
  roots.navCounts,
  roots.revenue,
  roots.agents,
];

export function useProductMutations() {
  const invalidate = useInvalidate();
  const refresh = () => invalidate(...CATALOG);
  return {
    create: useMutation({
      mutationFn: (values: ProductFormValues) => catalog.createProduct(values),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({ id, values }: { id: string; values: ProductFormValues }) =>
        catalog.updateProduct(id, values),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => catalog.deleteProduct(id),
      onSuccess: refresh,
    }),
    setStatus: useMutation({
      mutationFn: ({ ids, status }: { ids: string[]; status: ProductStatus }) =>
        insights.setProductsStatus(ids, status),
      onSuccess: refresh,
    }),
    removeMany: useMutation({
      mutationFn: (ids: string[]) => insights.deleteProducts(ids),
      onSuccess: refresh,
    }),
  };
}

/** Spreadsheet imports; both can touch products, stock and category counts. */
export function useCatalogImport() {
  const invalidate = useInvalidate();
  const refresh = () => invalidate(...CATALOG);
  return {
    products: useMutation({
      mutationFn: (rows: SheetRow[]) => catalogImport.importProducts(rows),
      onSuccess: refresh,
    }),
    categories: useMutation({
      mutationFn: (rows: SheetRow[]) => catalogImport.importCategories(rows),
      onSuccess: refresh,
    }),
  };
}

export function useCategoryMutations() {
  const invalidate = useInvalidate();
  const refresh = () => invalidate(roots.categories, roots.products);
  return {
    save: useMutation({
      mutationFn: ({ values, id }: { values: CategoryFormValues; id?: string }) =>
        catalog.saveCategory(values, id),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => catalog.deleteCategory(id),
      onSuccess: refresh,
    }),
    saveLanding: useMutation({
      mutationFn: ({ id, landing }: { id: string; landing: AisleLandingValues | null }) =>
        catalog.saveAisleLanding(id, landing),
      onSuccess: refresh,
    }),
  };
}

export function useRecountReservations() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => catalog.recountReservations(),
    onSuccess: () => invalidate(roots.inventory, roots.products, roots.product, roots.navCounts),
  });
}

export function useAdjustStock() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ productId, values }: { productId: string; values: StockAdjustmentValues }) =>
      catalog.adjustStock(productId, values),
    onSuccess: () =>
      invalidate(
        roots.inventory,
        roots.products,
        roots.product,
        roots.dashboard,
        roots.navCounts,
        roots.adjustments,
      ),
  });
}

export function useOrderMutations() {
  const invalidate = useInvalidate();
  const refresh = () => invalidate(...ORDERS);
  return {
    updateStatus: useMutation({
      mutationFn: ({ id, values }: { id: string; values: OrderStatusUpdateValues }) =>
        ops.updateOrderStatus(id, values),
      onSuccess: refresh,
    }),
    advanceMany: useMutation({
      mutationFn: ({ ids, status }: { ids: string[]; status: OrderStatus }) =>
        insights.advanceOrders(ids, status),
      onSuccess: refresh,
    }),
  };
}

export function useCustomerMutations() {
  const invalidate = useInvalidate();
  const refresh = () => invalidate(roots.customers, roots.customer, roots.dashboard);
  return {
    update: useMutation({
      mutationFn: ({ id, values }: { id: string; values: AdminCustomerValues }) =>
        ops.updateCustomer(id, values),
      onSuccess: refresh,
    }),
    setStatus: useMutation({
      mutationFn: ({ ids, status }: { ids: string[]; status: CustomerStatus }) =>
        insights.setCustomersStatus(ids, status),
      onSuccess: refresh,
    }),
  };
}

export function useDeliveryMutations() {
  const invalidate = useInvalidate();
  const refresh = () => invalidate(...ORDERS);
  return {
    update: useMutation({
      mutationFn: ({ id, values }: { id: string; values: DeliveryAssignmentValues }) =>
        ops.updateDelivery(id, values),
      onSuccess: refresh,
    }),
    assignMany: useMutation({
      mutationFn: ({
        ids,
        agentId,
        status,
      }: {
        ids: string[];
        agentId: string;
        status?: DeliveryStatus;
      }) => insights.assignDeliveries(ids, agentId, status),
      onSuccess: refresh,
    }),
  };
}
